/**
 * Re-export react-icons with proper React 18 typing
 *
 * react-icons returns ReactNode which is incompatible with stricter
 * React 18 JSX element typing. This wrapper provides proper typing.
 */
import React from "react";
import * as FaIcons from "react-icons/fa";

// Create a properly typed icon component wrapper
type IconProps = {
  className?: string;
  style?: React.CSSProperties;
  size?: string | number;
  color?: string;
  title?: string;
};

// Helper to create a typed icon component
const createIcon = (Icon: any): React.FC<IconProps> => {
  return (props) => <Icon {...props} />;
};

// Export commonly used icons with proper typing
export const FaExclamationTriangle = createIcon(FaIcons.FaExclamationTriangle);
export const FaSpinner = createIcon(FaIcons.FaSpinner);
export const FaMicrophone = createIcon(FaIcons.FaMicrophone);
export const FaPlus = createIcon(FaIcons.FaPlus);
export const FaBuilding = createIcon(FaIcons.FaBuilding);
export const FaCog = createIcon(FaIcons.FaCog);
export const FaPlay = createIcon(FaIcons.FaPlay);
export const FaPause = createIcon(FaIcons.FaPause);
export const FaChevronDown = createIcon(FaIcons.FaChevronDown);
export const FaChevronUp = createIcon(FaIcons.FaChevronUp);
export const FaLock = createIcon(FaIcons.FaLock);
export const FaRedo = createIcon(FaIcons.FaRedo);
export const FaListAlt = createIcon(FaIcons.FaListAlt);
export const FaTimes = createIcon(FaIcons.FaTimes);
export const FaCheck = createIcon(FaIcons.FaCheck);
export const FaTrash = createIcon(FaIcons.FaTrash);
export const FaEdit = createIcon(FaIcons.FaEdit);
export const FaSave = createIcon(FaIcons.FaSave);
export const FaPencilAlt = createIcon(FaIcons.FaPencilAlt);
export const FaDownload = createIcon(FaIcons.FaDownload);
export const FaUpload = createIcon(FaIcons.FaUpload);
export const FaSearch = createIcon(FaIcons.FaSearch);
export const FaUser = createIcon(FaIcons.FaUser);
export const FaSignOutAlt = createIcon(FaIcons.FaSignOutAlt);
export const FaChevronLeft = createIcon(FaIcons.FaChevronLeft);
export const FaChevronRight = createIcon(FaIcons.FaChevronRight);
export const FaExpand = createIcon(FaIcons.FaExpand);
export const FaCompress = createIcon(FaIcons.FaCompress);
export const FaEye = createIcon(FaIcons.FaEye);
export const FaEyeSlash = createIcon(FaIcons.FaEyeSlash);
export const FaCopy = createIcon(FaIcons.FaCopy);
export const FaClipboard = createIcon(FaIcons.FaClipboard);
export const FaFolder = createIcon(FaIcons.FaFolder);
export const FaFile = createIcon(FaIcons.FaFile);
export const FaFileAlt = createIcon(FaIcons.FaFileAlt);
export const FaInfo = createIcon(FaIcons.FaInfo);
export const FaInfoCircle = createIcon(FaIcons.FaInfoCircle);
export const FaQuestionCircle = createIcon(FaIcons.FaQuestionCircle);
export const FaExclamationCircle = createIcon(FaIcons.FaExclamationCircle);
export const FaCheckCircle = createIcon(FaIcons.FaCheckCircle);
export const FaTimesCircle = createIcon(FaIcons.FaTimesCircle);
export const FaBars = createIcon(FaIcons.FaBars);
export const FaHome = createIcon(FaIcons.FaHome);
export const FaArrowLeft = createIcon(FaIcons.FaArrowLeft);
export const FaArrowRight = createIcon(FaIcons.FaArrowRight);
export const FaArrowUp = createIcon(FaIcons.FaArrowUp);
export const FaArrowDown = createIcon(FaIcons.FaArrowDown);
export const FaAngleDoubleLeft = createIcon(FaIcons.FaAngleDoubleLeft);
export const FaAngleDoubleRight = createIcon(FaIcons.FaAngleDoubleRight);
export const FaDatabase = createIcon(FaIcons.FaDatabase);
export const FaEllipsisV = createIcon(FaIcons.FaEllipsisV);
export const FaEraser = createIcon(FaIcons.FaEraser);
export const FaExternalLinkAlt = createIcon(FaIcons.FaExternalLinkAlt);
export const FaFilter = createIcon(FaIcons.FaFilter);
export const FaGoogle = createIcon(FaIcons.FaGoogle);
export const FaLayerGroup = createIcon(FaIcons.FaLayerGroup);
export const FaLightbulb = createIcon(FaIcons.FaLightbulb);
export const FaMagic = createIcon(FaIcons.FaMagic);
export const FaPaperPlane = createIcon(FaIcons.FaPaperPlane);
export const FaRuler = createIcon(FaIcons.FaRuler);
export const FaSync = createIcon(FaIcons.FaSync);
export const FaWindowMaximize = createIcon(FaIcons.FaWindowMaximize);
export const FaLink = createIcon(FaIcons.FaLink);
export const FaUnlink = createIcon(FaIcons.FaUnlink);
export const FaClock = createIcon(FaIcons.FaClock);
export const FaSun = createIcon(FaIcons.FaSun);
export const FaArrowsAlt = createIcon(FaIcons.FaArrowsAlt);
export const FaArrowsAltH = createIcon(FaIcons.FaArrowsAltH);
export const FaSearchPlus = createIcon(FaIcons.FaSearchPlus);
export const FaUndo = createIcon(FaIcons.FaUndo);
export const FaAdjust = createIcon(FaIcons.FaAdjust);
export const FaCrosshairs = createIcon(FaIcons.FaCrosshairs);
export const FaChartBar = createIcon(FaIcons.FaChartBar);
export const FaTag = createIcon(FaIcons.FaTag);
export const FaStar = createIcon(FaIcons.FaStar);
export const FaTh = createIcon(FaIcons.FaTh);
export const FaThLarge = createIcon(FaIcons.FaThLarge);
export const FaColumns = createIcon(FaIcons.FaColumns);
export const FaSquare = createIcon(FaIcons.FaSquare);
export const FaVideo = createIcon(FaIcons.FaVideo);
export const FaStop = createIcon(FaIcons.FaStop);
export const FaStepForward = createIcon(FaIcons.FaStepForward);
export const FaCircle = createIcon(FaIcons.FaCircle);
export const FaCamera = createIcon(FaIcons.FaCamera);
export const FaMicrophoneSlash = createIcon(FaIcons.FaMicrophoneSlash);
export const FaVideoSlash = createIcon(FaIcons.FaVideoSlash);
export const FaBookmark = createIcon(FaIcons.FaBookmark);
export const FaRegBookmark = createIcon(FaIcons.FaRegBookmark);
export const FaSortAmountDown = createIcon(FaIcons.FaSortAmountDown);
